import type { FC } from "react";
import { useMemo, useRef, useState } from "react";

import { useCurrentStateAndParams } from "@uirouter/react";

import { ChipDownContainer } from "@similarweb/ui-components";

import { translate } from "app/filters/i18nFilter";

import { ExactUrlFilterContent } from "./ExactUrlFilterContent";
import type { IExactMatchFilterProps } from "./ExactUrlFilterTypes";
import { EBooleanSearchFilterTypes } from "./ExactUrlFilterTypes";

export const ExactUrlFilter: FC<IExactMatchFilterProps> = ({
    exactURL,
    onFilterChange,
    type = EBooleanSearchFilterTypes.URL,
    inputFieldTooltip = `digitalsuite.backlinks.table.filter.url.input.tooltip`,
    inputFieldTitle = `digitalsuite.backlinks.table.filter.url.input.title`,
}) => {
    const filterPopupRef = useRef<{ closePopup: () => void }>(null);

    const [selectedURL, setSelectedURL] = useState<string>(exactURL);
    const [currentURL, setCurrentURL] = useState<string>(exactURL);
    const [isApplyButtonDisabled, setIsApplyButtonDisabled] = useState<boolean>(!selectedURL);
    const [inputHasError, setInputHasError] = useState<boolean>(false);

    const minValidationStr = 5;

    const { params: routerParams } = useCurrentStateAndParams();
    const validationRegexp = useMemo(
        () => new RegExp(`^(https?:\\/\\/)?(.+\\.)?` + routerParams.key),
        [routerParams.key],
    );

    const isValidURL = (url: string) => {
        return validationRegexp.test(url);
    };

    const updateCurrentURL = (url: string) => {
        if (url.length < minValidationStr) {
            setIsApplyButtonDisabled(true);
            return;
        }

        const isUrlValid = isValidURL(url);

        setIsApplyButtonDisabled(!isUrlValid);
        setInputHasError(!isUrlValid);

        if (isUrlValid) {
            setCurrentURL(url);
        }
    };

    const handleChange = (val: string) => {
        updateCurrentURL(val);
    };

    const handleInputKeyUp = (evt: KeyboardEvent) => {
        switch (evt.key) {
            case "Enter":
                onApplyClick();
                break;
            case "Escape":
                onCloseFilterClick(evt);
                break;
            default:
                break;
        }
    };

    const resetSelection = () => {
        setSelectedURL("");
    };

    const onCloseFilterClick = (e: Event) => {
        e.stopPropagation();
        onFilterChange("");
        resetSelection();
        filterPopupRef.current?.closePopup();
    };

    const onCancelClick = () => {
        filterPopupRef.current?.closePopup();
    };

    const onApplyClick = () => {
        onFilterChange(currentURL);
        setSelectedURL(currentURL);
        filterPopupRef.current?.closePopup();
    };

    const buttonText = translate("digitalsuite.backlinks.table.filter.url.empty_title");
    const selectedText = translate(`boolean.search.chip.filter.${type}.selected.include.one`, {
        name: selectedURL,
    });

    return (
        <ChipDownContainer
            selectedText={selectedURL ? selectedText : ""}
            onCloseItem={(e) => onCloseFilterClick(e as unknown as Event)}
            buttonText={buttonText}
            ref={filterPopupRef}
            width={508}
            noResultsMessage={translate("dropdown.no.results")}
        >
            {[
                <ExactUrlFilterContent
                    key={"exact-url-filter"}
                    selectedURL={selectedURL}
                    inputFieldTitle={inputFieldTitle}
                    inputFieldTooltip={inputFieldTooltip}
                    handleInputKeyUp={handleInputKeyUp}
                    handleChange={handleChange}
                    inputHasError={inputHasError}
                    errorMessage={translate("backlinks.targeturl.error", {
                        URL: `${routerParams.key}/exact-url/path?somedata=true`,
                    })}
                    isApplyButtonDisabled={isApplyButtonDisabled}
                    onApplyClick={onApplyClick}
                    onCancelClick={onCancelClick}
                />,
            ]}
        </ChipDownContainer>
    );
};
